import React, { useEffect, useState } from 'react'
import type { ICategory, IAttribute, IItem } from '../../IItem'
import { Attributes } from './Attributes/Attributes'
import {
  getCategoryId,
  getIsValidCategoryLevel,
  getNewItemCategoryObject,
} from './Category.helpers'
import { CategoryPicker } from './CategoryPicker'
import ErrorLabel from '../ErrorLabel'
import Label from '../../../Components/Label'
import type { ISellItem } from '../../../SellView/ISellView'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../../../Store/hooks'
import { getCategories, getAttributes } from '../../../UI/UI.actions'
import Condition from '../Condition/Condition'
import { conditionAttributeId } from '../../../Constants/Attributes'

interface IAttributesState {
  [categoryId: string]: IAttribute[]
}
type ICategoryComponent = {
  updateStateByItem: (key: string, value: any) => void
  item: IItem | ISellItem
  noLabel?: boolean
  handleInputChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
}

const getDeepestSelectedCategory = (category: ICategory | undefined) => {
  if (category?.category && category.category.length > 0) {
    return getDeepestSelectedCategory(category.category[0])
  }
  return category
}

export const Category = ({
  updateStateByItem,
  item,
  noLabel,
  handleInputChange,
}: ICategoryComponent) => {
  const { t } = useTranslation()
  const categories = useAppSelector((state) => state.ui.categories)
  const attributes = useAppSelector((state) => state.ui.attributes)
  const dispatch = useAppDispatch()
  const [selectableCategoriesByLevel, setSelectableCategoriesByLevel] =
    useState<Record<number, ICategory[] | undefined>>()
  const [attributeCategoryId, setAttributeCategoryId] = useState<string>()
  const [defaultAttributes, setDefaultAttributes] = useState([])
  const deepestSelectedCategory = getDeepestSelectedCategory(item.category)
  const isDeepestSelectedCategoryLast =
    deepestSelectedCategory?.isLastCategoryLevel
  const [openPicker, setOpenPicker] = useState<number>(0)

  const setSelectedAttributes = (
    selectedAttributes: any,
    categoryId: string
  ) => {
    const attributeState: IAttributesState = selectedAttributes
    const attributesToSend: any[] = []
    //Add selected attributes
    for (const id in attributeState) {
      attributesToSend.push({
        id,
        key: id,
        possibleTermValues: attributeState[id],
        minNumberOfValues: attributes[attributeCategoryId!]!.find(
          (c) => c.id.toString() === id
        )
          ? attributes[attributeCategoryId!]!.find(
              (c) => c.id.toString() === id
            )!.minNumberOfValues
          : 0,
        maxNumberOfValues: attributes[attributeCategoryId!]!.find(
          (c) => c.id.toString() === id
        )
          ? attributes[attributeCategoryId!]!.find(
              (c) => c.id.toString() === id
            )!.maxNumberOfValues
          : 0,
      })
    }
    //Add mandatory attributes even if they are not set (for list validation)
    if (attributes![categoryId!] && attributes![categoryId!].length > 0) {
      attributes[categoryId!]!.forEach((attribute) => {
        if (
          !attributesToSend.find((c) => c.id === attribute.id.toString()) &&
          attribute.minNumberOfValues > 0
        ) {
          let mandatoryAttribute = { ...attribute }
          mandatoryAttribute.possibleTermValues = []
          attributesToSend.push(mandatoryAttribute)
        }
      })
    }
    updateStateByItem('attributes', attributesToSend)
  }

  useEffect(() => {
    dispatch(getCategories())
  }, [categories.length, dispatch])

  useEffect(() => {
    if (deepestSelectedCategory && isDeepestSelectedCategoryLast) {
      dispatch(getAttributes(deepestSelectedCategory.id))
      setAttributeCategoryId(deepestSelectedCategory.id)
      if (item.attributes) {
        setDefaultAttributes(item.attributes as any)
      }
    }
  }, [
    deepestSelectedCategory,
    dispatch,
    isDeepestSelectedCategoryLast,
    item.attributes,
  ])

  useEffect(() => {
    if (item.category || categories.length > 0) {
      const firstCategory = item.category
      const secondCategory = firstCategory?.category?.[0]
      const thirdCategory = secondCategory?.category?.[0]

      const selectableCategoriesForLevel1 = categories
      const selectableCategoriesForLevel2 = categories.find(
        (c) => c.id === firstCategory?.id
      )?.category
      const selectableCategoriesForLevel3 = selectableCategoriesForLevel2?.find(
        (c) => c.id === secondCategory?.id
      )?.category
      const selectableCategoriesForLevel4 = selectableCategoriesForLevel3?.find(
        (c) => c.id === thirdCategory?.id
      )?.category

      setSelectableCategoriesByLevel({
        1: selectableCategoriesForLevel1,
        2: selectableCategoriesForLevel2,
        3: selectableCategoriesForLevel3,
        4: selectableCategoriesForLevel4,
      })
    }
  }, [item.category, categories])

  const updateItemCategory = (c: ICategory | undefined, level: number) => {
    if (!c) return
    setOpenPicker(level + 1)
    updateStateByItem('attributes', undefined)
    const newCategoryObject = getNewItemCategoryObject(item.category, c, level)
    updateStateByItem('category', newCategoryObject)
    setAttributeCategoryId('')
  }

  const handlePickerClose = () => {
    // When a picker is manually closed, set openPicker 0 to close all pickers
    setOpenPicker(0)
  }

  return (
    <div className="mt-2">
      {item.validationResult &&
      !item.validationResult?.properties.category &&
      !noLabel ? (
        <ErrorLabel
          label={t('itemsForm_category_label') + '*'}
          description={t('itemsForm_category_error')}
        />
      ) : !noLabel ? (
        <Label label={t('itemsForm_category_label') + '*'} />
      ) : (
        ''
      )}
      <div className="row d-flex gap-y-1">
        <div className="col-lg-3">
          <CategoryPicker
            item={item}
            categories={selectableCategoriesByLevel?.[1]}
            level={1}
            defaultValueId={getCategoryId(1, item.category)}
            isValidCategoryLevel={getIsValidCategoryLevel(1, item.category)}
            updateItemCategory={updateItemCategory}
            isOpen={openPicker === 1}
            onClose={handlePickerClose}
          />
        </div>
        <div className="col-lg-3">
          <CategoryPicker
            item={item}
            categories={selectableCategoriesByLevel?.[2]}
            level={2}
            defaultValueId={getCategoryId(2, item.category)}
            isValidCategoryLevel={getIsValidCategoryLevel(2, item.category)}
            updateItemCategory={updateItemCategory}
            isOpen={openPicker === 2}
            onClose={handlePickerClose}
          />
        </div>
        <div className="col-lg-3">
          <CategoryPicker
            item={item}
            categories={selectableCategoriesByLevel?.[3]}
            level={3}
            defaultValueId={getCategoryId(3, item.category)}
            isValidCategoryLevel={getIsValidCategoryLevel(3, item.category)}
            updateItemCategory={updateItemCategory}
            isOpen={openPicker === 3}
            onClose={handlePickerClose}
          />
        </div>
        <div className="col-lg-3">
          <CategoryPicker
            item={item}
            categories={selectableCategoriesByLevel?.[4]}
            level={4}
            defaultValueId={getCategoryId(4, item.category)}
            isValidCategoryLevel={getIsValidCategoryLevel(4, item.category)}
            updateItemCategory={updateItemCategory}
            isOpen={openPicker === 4}
            onClose={handlePickerClose}
          />
        </div>
      </div>
      {attributeCategoryId && (
        <>
          {handleInputChange &&
            !attributes[attributeCategoryId]?.some(
              (attr) => attr.id === conditionAttributeId
            ) && (
              <div className="row mt-3">
                <div className="col-12">
                  {item.validationResult &&
                  !item.validationResult?.properties.condition ? (
                    <ErrorLabel
                      label={t('itemsForm_condition_label') + '*'}
                      description={t('itemsForm_condition_labelError')}
                    />
                  ) : (
                    <Label label={t('itemsForm_condition_label') + '*'} />
                  )}
                  <Condition
                    item={item}
                    handleInputChange={handleInputChange}
                  />
                </div>
              </div>
            )}
          <Attributes
            categoryId={attributeCategoryId}
            attributes={attributes}
            item={item}
            setSelectedAttributes={setSelectedAttributes}
            defaultAttributes={defaultAttributes}
          />
        </>
      )}
    </div>
  )
}
